import * as React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';

type Props = {
  num?: number;
  blogId?: string;
  img?: string;
  url?: string;
  title?: string;
  width?: number;
  height?: number;
  bottom?: boolean;
  recommend?: number;
  children?: React.ReactNode;
};

const RecommendItem = styled.li`
  display: block;
  width: 100%;
  counter-reset: number 0;
  margin-bottom: ${({ theme }) => theme.spaces.m};
`;

const RecommendImgWrap = styled.div`
  width: 100%;
  height: auto;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-conten: center;
  border: 4px solid ${({ theme }) => theme.borderColors.default};
`;

const RecommendImg = styled.img`
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  vertical-align: bottom;
  object-fit: contain;
  object-position: 50% 50%;
`;

const RecommendLink = styled(Link)<Props>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spaces.m};
  width: 100%;
  margin: 0;
  font-weight: ${({ theme, bottom }) =>
    bottom ? theme.fontWeights.bold : theme.fontWeights.normal};
  background: ${({ theme, bottom }) =>
    bottom ? theme.backgroundColors.white : 'none'};
  padding: ${({ theme, bottom }) => (bottom ? theme.spaces.s : 0)};
  transition-duration: ${({ theme }) => theme.transitions.normal};
  border: 1px solid transparent;
  border-radius: ${({ theme, bottom }) => (bottom ? theme.radii.s : 0)};

  ${RecommendImg}, ${RecommendImgWrap} {
    transition-duration: ${({ theme }) => theme.transitions.normal};
  }

  &:hover {
    text-decoration: ${({ bottom }) => (bottom ? 'normal' : 'underline')};
    background-color: ${({ theme, bottom }) =>
      bottom ? theme.borderColors.white : 'none'};
    border: 1px solid
      ${({ theme, bottom }) =>
        bottom ? theme.borderColors.primary : 'transparent'};

    ${RecommendImg} {
      transform: scale(1.1);
    }

    ${RecommendImgWrap} {
      border-color: ${({ theme }) => theme.borderColors.primary};
    }
  }
`;

const RecommendTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.s};
  word-break: break-word;
  width: 100%;
  overflow: hidden;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const RecommendRank = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background-color: ${({ theme }) => theme.backgroundColors.primary};
  color: ${({ theme }) => theme.fontColors.white};
  font-size: ${({ theme }) => theme.fontSizes.m};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

export const Recommend: React.FC<Props> = (props) => {
  if (props.recommend && (props.recommend < 1 || props.recommend > 5)) {
    return null;
  }

  return (
    <RecommendItem key={props.blogId}>
      <RecommendLink to={`/blog/${props.blogId}`} bottom={props.bottom && true}>
        <RecommendRank>{props.num}</RecommendRank>
        <RecommendImgWrap>
          <RecommendImg
            src={`${props.img}?q=50&w=640`}
            alt={props.title || 'No title'}
            width="100%"
            height="100%"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
              aspectRatio: '16/9',
              backgroundColor: '#f5f6f7',
            }}
          />
        </RecommendImgWrap>
        <RecommendTitle>{props.title}</RecommendTitle>
      </RecommendLink>
    </RecommendItem>
  );
};
